/**
 * Unified amount-due-now resolution for the configure summary, pay button, and
 * Stripe Elements. Two paths:
 *
 *   1. Existing sub on cycle → use PreviewCheckout's `dueNow` (Stripe-authoritative,
 *      covers tier deltas, addon add/remove, and per-env DE subs in one number).
 *   2. New user (no anchor sub) → use catalog `grandTotalCycleCents`.
 *
 * Frontend never bolts addon prices on top of the preview — the BE preview
 * already includes them via multi-item + new_subs.
 */

/**
 * Discount-scoped relaxation of the legacy "live preview needs an anchor sub"
 * display gate (shouldShowLiveInvoicePreview): a first-time buyer has no
 * active subscription, but useInvoicePreviewSync still runs the
 * PreviewCheckout RPC and its reply carries Stripe-real subtotal/discount/
 * dueNow cents. When an applied code produced a settled positive discount on
 * a non-trial cart, those cents are authoritative for what today's charge
 * will be — the ledger and every due-now surface switch to them together.
 * Deliberately narrow: no code, trials, in-flight quotes and zero-discount
 * replies all resolve false, keeping every no-discount path exactly on its
 * current catalog behavior.
 *
 * @param {object} params
 * @param {boolean} params.couponApplied  — a discount code is currently applied
 * @param {boolean} [params.trialApplies]  — trial carts never carry a discount
 * @param {{ discount?: number } | null} params.invoicePreview
 * @param {boolean} params.invoiceLoading
 */
export function shouldUseDiscountedPreview({ couponApplied, trialApplies = false, invoicePreview, invoiceLoading }) {
  if (!couponApplied || trialApplies || invoiceLoading || !invoicePreview) return false;
  return Math.abs(Number(invoicePreview.discount || 0)) > 0;
}

// BM's `dueNow` may map to Stripe's `subtotal` (pre-credit/discount). Mirror
// Stripe's `amount_due` here so the UI matches the invoice the user will
// actually be charged: subtotal − discount − applied account balance.
function previewAmountDue(invoicePreview, availableCredit) {
  const subtotal = Number(invoicePreview.subtotal || 0);
  const discount = Math.abs(Number(invoicePreview.discount || 0));
  const credit = resolveEffectiveCredit(invoicePreview, subtotal, availableCredit);
  if (subtotal > 0) {
    return Math.max(0, subtotal - discount - credit);
  }

  const previewDue = invoicePreview.dueNow != null ? Number(invoicePreview.dueNow) : 0;
  return Math.max(0, previewDue - discount - credit);
}

/**
 * @param {object} params
 * @param {boolean} params.authCheckout
 * @param {object | null} params.activeSubscription
 * @param {{ dueNow?: number } | null} params.invoicePreview
 * @param {boolean} params.invoiceLoading
 * @param {number} params.grandTotalCycleCents  — catalog total for new-user path
 * @param {number} [params.availableCredit]  — account credit deducted from the total
 * @param {boolean} [params.trialApplies]  — free trial → $0 due today
 * @param {boolean} [params.couponApplied]  — discount code applied; lets a settled
 *   discounted preview drive the new-user figure (see shouldUseDiscountedPreview)
 */
export function resolveAmountDueNow({
  authCheckout,
  activeSubscription,
  invoicePreview,
  invoiceLoading,
  grandTotalCycleCents = 0,
  availableCredit = 0,
  trialApplies = false,
  couponApplied = false
}) {
  const catalogGrandTotal = Number(grandTotalCycleCents) || 0;

  if (!authCheckout || !activeSubscription?.id) {
    // Free trial → $0 charged today; the cycle total bills only when the trial
    // ends. (Card/standard path. The crypto checkout pays the first period
    // upfront, so its caller must NOT pass trialApplies.)
    //
    // But a trial is granted per CREATED SUBSCRIPTION, not per cart, and
    // `trialApplies` is true as soon as ANY created sub is trial-eligible. A
    // cart of paid accounts plus a Futures trial therefore quoted $0 while
    // checkout charged for the accounts — the quote understating the charge.
    // The settled preview prices each new sub separately (the trialing one at
    // $0, the rest in full), so it decides whenever we have it; an all-trial
    // cart reaches $0 through that same path.
    if (trialApplies) {
      if (!invoiceLoading && invoicePreview) return previewAmountDue(invoicePreview, availableCredit);
      return 0;
    }
    // Applied code with a settled, genuinely discounted preview → the preview
    // is Stripe-authoritative even without an anchor sub. Same math as the
    // existing-sub path below, so the shown total matches the actual charge.
    if (shouldUseDiscountedPreview({ couponApplied, trialApplies, invoicePreview, invoiceLoading })) {
      return previewAmountDue(invoicePreview, availableCredit);
    }
    // Deduct available account credit from catalog total so the "due today"
    // figure matches what Stripe will actually charge at invoice finalization.
    const credit = Math.abs(Number(availableCredit) || 0);
    return Math.max(0, catalogGrandTotal - credit);
  }

  if (!invoicePreview || invoiceLoading) {
    // Modify flow: never fall back to the catalog grand total — the hydrated
    // cart mirrors the existing sub, so showing it would re-bill what they
    // already pay. Wait for the preview; 0 in the meantime keeps UI honest.
    return 0;
  }

  return previewAmountDue(invoicePreview, availableCredit);
}

/**
 * Amount a crypto checkout must collect, in cents.
 *
 * Coingate charges this and the same figure is credited to the Stripe customer
 * balance, which then funds the invoice the cron's subscription create raises —
 * so it has to equal what Stripe will actually bill, discount included.
 *
 * A Dedicated Environment added to an existing account is a brand-new
 * subscription with no proration, so its cost is deterministic from the catalog
 * and that shortcut skipped the preview entirely. With a code applied that
 * shortcut charged list price: the drawer quoted $63 while Coingate asked for
 * $90, and the difference silently became leftover account credit. Whenever the
 * settled preview carries a real discount for this cart, it wins.
 *
 * @param {Record<string, any>} params — every resolveAmountDueNow param, plus
 *   `dedicatedOnlyCatalogCents`: catalog cost of a DE-only addition (0 when the
 *   cart isn't that shape)
 * @returns {number}
 */
export function resolveCryptoPurchaseTotal(params) {
  const catalogOnly = Number(params && params.dedicatedOnlyCatalogCents) || 0;
  if (catalogOnly > 0 && !shouldUseDiscountedPreview(params)) return catalogOnly;
  return resolveAmountDueNow(params);
}

/**
 * Stripe's `appliedBalance` only gets populated when the invoice is finalized.
 * For an *upcoming* preview the credit lives in `startingBalance` (the user's
 * available account credit). Mirror Stripe's finalize-time application: clamp
 * the credit to the invoice subtotal so it never exceeds what's actually owed.
 */
export function resolveEffectiveCredit(invoicePreview, subtotal, availableCredit = 0) {
  // Credit can never exceed what's owed — clamp every path to the subtotal.
  const cap = Number(subtotal) || 0;
  const clamp = (amount) => (cap > 0 ? Math.min(amount, cap) : amount);

  const applied = invoicePreview ? Math.abs(Number(invoicePreview.appliedBalance || 0)) : 0;
  if (applied > 0) return clamp(applied);
  // PreviewCheckout doesn't always populate `startingBalance`/`appliedBalance`;
  // fall back to the user's account credit from BillingOverview so the UI still
  // reflects what Stripe will apply at invoice finalization.
  const balance = (invoicePreview ? Math.abs(Number(invoicePreview.startingBalance || 0)) : 0) || Math.abs(Number(availableCredit || 0));
  if (balance <= 0) return 0;
  return clamp(balance);
}

export function shouldShowLiveInvoicePreview({ authCheckout, activeSubscription, invoicePreview, invoiceLoading }) {
  if (!authCheckout || !activeSubscription?.id || invoiceLoading) return false;
  if (!invoicePreview) return false;
  // Accept any meaningful preview signal — `dueNow` may legitimately be 0/null
  // when the change is fully covered by credits or balanced charges, but the
  // user still needs to see the credit / discount / line breakdown.
  return Boolean(
    invoicePreview.dueNow != null ||
      (Array.isArray(invoicePreview.linesList) && invoicePreview.linesList.length > 0) ||
      (invoicePreview.discount || 0) !== 0 ||
      (invoicePreview.appliedBalance || 0) > 0 ||
      (invoicePreview.startingBalance || 0) > 0 ||
      (invoicePreview.subtotal || 0) > 0
  );
}
